export type Lang = 'en' | 'fr' | 'es' | 'pt' | 'de' | 'ar' | 'zh';

export type TranslationKey =
  | 'brand.name'
  | 'brand.console'
  | 'nav.pricing'
  | 'nav.signIn'
  | 'auth.heroTitle'
  | 'auth.heroSubtitle'
  | 'auth.feature.privateLinks'
  | 'auth.feature.customSlug'
  | 'auth.feature.editAnytime'
  | 'auth.note'
  | 'auth.loginTitle'
  | 'auth.registerTitle'
  | 'auth.loginSubtitle'
  | 'auth.registerSubtitle'
  | 'auth.tipGoogle'
  | 'auth.email'
  | 'auth.emailPlaceholder'
  | 'auth.password'
  | 'auth.passwordPlaceholder'
  | 'auth.wait'
  | 'auth.signIn'
  | 'auth.createAccount'
  | 'auth.or'
  | 'auth.continueWithGoogle'
  | 'auth.createFree'
  | 'auth.haveAccount'
  | 'status.apiOnline'
  | 'status.apiOffline'
  | 'plan.free'
  | 'plan.pro'
  | 'plan.current'
  | 'plan.upgrade'
  | 'plan.linksUsed'
  | 'plan.limitReachedTitle'
  | 'plan.limitReachedBody'
  | 'plan.upgradeCta'
  | 'action.logout'
  | 'action.shorten'
  | 'quickShorten.placeholder'
  | 'quickShorten.descLabel'
  | 'quickShorten.codeLabel'
  | 'quickShorten.codePlaceholder'
  | 'quickShorten.submit'
  | 'linkForm.createTitle'
  | 'linkForm.editTitle'
  | 'folder.new'
  | 'folder.uncategorized'
  | 'linkForm.destinationLabel'
  | 'linkForm.destinationPlaceholder'
  | 'linkForm.slugOptional'
  | 'linkForm.slug'
  | 'linkForm.slugPlaceholder'
  | 'linkForm.preview'
  | 'linkForm.save'
  | 'linkForm.descriptionLabel'
  | 'linkForm.descriptionPlaceholder'
  | 'linkForm.generating'
  | 'linkForm.generate'
  | 'linkForm.update'
  | 'linkForm.cancel'
  | 'linkForm.folderLabel'
  | 'linkForm.noFolder'
  | 'stats.linksCount'
  | 'stats.lastUpdated'
  | 'search.label'
  | 'search.placeholder'
  | 'loading.links'
  | 'toast.linksLoadFailed'
  | 'toast.linkCreated'
  | 'toast.createFailed'
  | 'toast.planLimit'
  | 'toast.linkUpdated'
  | 'toast.updateFailed'
  | 'confirm.delete'
  | 'toast.linkDeleted'
  | 'toast.deleteFailed'
  | 'table.title'
  | 'table.subtitle'
  | 'table.emptyTitle'
  | 'table.emptySubtitle'
  | 'table.slug'
  | 'table.destination'
  | 'table.clicks'
  | 'table.updated'
  | 'table.updated'
  | 'table.actions'
  | 'table.copy'
  | 'table.edit'
  | 'table.delete'
  | 'table.deleting'
  | 'table.linkSingular'
  | 'table.linkPlural'
  | 'table.updatedLabel'
  | 'pricing.badge'
  | 'pricing.title'
  | 'pricing.subtitle'
  | 'pricing.exampleTitle'
  | 'pricing.exampleSubtitle'
  | 'pricing.exampleHint'
  | 'pricing.ctaStart'
  | 'pricing.ctaBuyPro'
  | 'pricing.disclaimer'
  | 'pricing.plansLabel'
  | 'pricing.freeTitle'
  | 'pricing.freePrice'
  | 'pricing.freePeriod'
  | 'pricing.freeLede'
  | 'pricing.freeFeature1'
  | 'pricing.freeFeature2'
  | 'pricing.freeFeature3'
  | 'pricing.freeFeature4'
  | 'pricing.freeCta'
  | 'pricing.bestValue'
  | 'pricing.proTitle'
  | 'pricing.proPeriod'
  | 'pricing.proLede'
  | 'pricing.proFeature1'
  | 'pricing.proFeature2'
  | 'pricing.proFeature3'
  | 'pricing.proFeature4'
  | 'pricing.proCta'
  | 'pricing.proCtaAlt'
  | 'pricing.proNote'
  | 'pricing.faqTitle'
  | 'pricing.faqQ1'
  | 'pricing.faqA1'
  | 'pricing.faqQ2'
  | 'pricing.faqA2'
  | 'pricing.faqQ3'
  | 'pricing.faqA3'
  | 'pricing.faqQ4'
  | 'pricing.faqA4'
  | 'footer.legalNotice'
  | 'footer.privacy'
  | 'footer.terms'
  | 'footer.copyright'
  | 'footer.promo'
  | 'hero.inputPlaceholder'
  | 'hero.shortenButton'
  | 'hero.noCreditCard'
  | 'pricing.freeFeatureModify'
  | 'pricing.freeFeatureSlug'
  | 'hero.badge.editable'
  | 'hero.badge.noAds'
  | 'hero.badge.fast'
  | 'hero.badge.secure'
  | 'pricing.analyticsTitle'
  | 'pricing.analyticsDesc'
  | 'pricing.qrCodeTitle'
  | 'pricing.qrCodeDesc'
  | 'pricing.passwordTitle'
  | 'pricing.passwordDesc'
  | 'fab.createFirst'
  | 'folder.create'
  | 'folder.name'
  | 'folder.empty';

type TranslationTable = Record<TranslationKey, string>;

export const languages: Array<{ code: Lang; label: string; flag: string; locale: string; dir?: 'ltr' | 'rtl' }> = [
  { code: 'en', label: 'English', flag: '🇺🇸', locale: 'en-US' },
  { code: 'fr', label: 'Français', flag: '🇫🇷', locale: 'fr-FR' },
  { code: 'es', label: 'Español', flag: '🇪🇸', locale: 'es-ES' },
  { code: 'pt', label: 'Português', flag: '🇵🇹', locale: 'pt-PT' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪', locale: 'de-DE' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦', locale: 'ar-SA', dir: 'rtl' },
  { code: 'zh', label: '中文', flag: '🇨🇳', locale: 'zh-CN' },
];

const translations: Record<Lang, TranslationTable> = {
  en: {
    'brand.name': 'ShortURL',
    'brand.console': 'Console',
    'nav.pricing': 'Pricing',
    'nav.signIn': 'Sign in',
    'auth.heroTitle': 'Shorten, share, update.',
    'auth.heroSubtitle': 'Create short links on <strong>{{domain}}</strong> and change the destination anytime.',
    'hero.inputPlaceholder': 'Paste your long link here...',
    'hero.shortenButton': 'Shorten',
    'hero.noCreditCard': 'No credit card required. <strong>Free forever.</strong>',
    'auth.feature.privateLinks': 'Private links (one account = your links)',
    'auth.feature.customSlug': 'Custom slug',
    'auth.feature.editAnytime': 'Editable anytime',
    'auth.note': 'An account is required to protect your links and show only your slugs.',
    'auth.loginTitle': 'Sign in',
    'auth.registerTitle': 'Create an account',
    'auth.loginSubtitle': 'Sign in to access your dashboard.',
    'auth.registerSubtitle': 'Create an account to manage your links securely.',
    'auth.tipGoogle': 'Tip: you can also use “Continue with Google”.',
    'auth.email': 'Email',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.password': 'Password',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': 'Please wait…',
    'auth.signIn': 'Sign in',
    'auth.createAccount': 'Create account',
    'auth.or': 'or',
    'auth.continueWithGoogle': 'Continue with Google',
    'auth.createFree': 'Create a free account',
    'auth.haveAccount': 'Already have an account? Sign in',
    'status.apiOnline': 'API online',
    'status.apiOffline': 'API offline',
    'plan.free': 'Free',
    'plan.pro': 'Pro',
    'plan.current': 'Current plan',
    'plan.upgrade': 'Upgrade',
    'plan.linksUsed': '{{used}} / {{limit}} links used',
    'plan.limitReachedTitle': 'Limit reached',
    'plan.limitReachedBody': 'You’ve reached the Free plan limit. Upgrade to Pro to create unlimited links.',
    'plan.upgradeCta': 'Unlock Pro',
    'action.logout': 'Log out',
    'action.shorten': 'Shorten a link',
    'quickShorten.placeholder': 'Paste a link to shorten...',
    'quickShorten.descLabel': 'Description',
    'quickShorten.codeLabel': 'Code',
    'quickShorten.codePlaceholder': 'Password (optional)',
    'quickShorten.submit': 'Shorten this link',
    'linkForm.createTitle': 'Create a link',
    'linkForm.editTitle': 'Edit link',
    'folder.new': 'New folder',
    'linkForm.destinationLabel': 'Destination (full URL)',
    'linkForm.destinationPlaceholder': 'https://example.com/article',
    'linkForm.slugOptional': 'Custom alias (optional)',
    'linkForm.slug': 'Alias',
    'linkForm.slugPlaceholder': 'my-alias',
    'linkForm.preview': 'Preview:',
    'linkForm.save': 'Save changes',
    'linkForm.descriptionLabel': 'Description (optional)',
    'linkForm.descriptionPlaceholder': 'Private notes…',
    'linkForm.generating': 'Working…',
    'linkForm.generate': 'Generate',
    'linkForm.update': 'Update',
    'linkForm.cancel': 'Cancel',
    'linkForm.folderLabel': 'Folder',
    'linkForm.noFolder': 'No folder',
    'stats.linksCount': 'Links',
    'stats.lastUpdated': 'Last updated',
    'search.label': 'Search a link',
    'search.placeholder': 'Search...',
    'loading.links': 'Loading links…',
    'toast.linksLoadFailed': 'Unable to load links',
    'toast.linkCreated': 'Link created',
    'toast.createFailed': 'Creation failed. Check the slug.',
    'toast.planLimit': 'Free plan limit reached. Upgrade to Pro to create more links.',
    'toast.linkUpdated': 'Link updated',
    'toast.updateFailed': 'Update failed',
    'confirm.delete': 'Delete {{slug}}?',
    'toast.linkDeleted': 'Link deleted',
    'toast.deleteFailed': 'Delete failed',
    'table.title': 'Your links',
    'table.subtitle': 'Manage your slugs and update destinations in 1 click.',
    'table.emptyTitle': 'Your links',
    'table.emptySubtitle': 'No links yet. Create your first short link.',
    'fab.createFirst': 'Create your first link',
    'table.slug': 'Alias',
    'table.destination': 'Destination',
    'table.clicks': 'Clicks',
    'table.updated': 'Updated',
    'table.actions': 'Actions',
    'table.copy': 'Copy',
    'table.edit': 'Edit',
    'table.delete': 'Delete',
    'table.deleting': 'Deleting…',
    'table.linkSingular': 'link',
    'table.linkPlural': 'links',
    'table.updatedLabel': 'Updated',
    'pricing.badge': 'Simple, fast, built for you',
    'pricing.title': 'Shorten, share, update.',
    'pricing.subtitle': 'Create short links on <strong>{{domain}}</strong> and change the destination anytime.',
    'pricing.exampleTitle': 'Example in action',
    'pricing.exampleSubtitle': 'Turn long URLs into clean short links you can edit later.',
    'pricing.exampleHint': 'Place your screenshot in /admin-spa/public/shorturl-preview.png.',
    'pricing.ctaStart': 'Start for free',
    'pricing.ctaBuyPro': 'Buy Pro',
    'pricing.disclaimer': 'Pro is a one-time purchase. No subscriptions.',
    'pricing.plansLabel': 'Plans',
    'pricing.freeTitle': 'Free',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ forever',
    'pricing.freeLede': 'Perfect to get started and share a few links.',
    'pricing.freeFeature1': 'Up to 5 links',
    'pricing.freeFeature2': 'Custom slugs',
    'pricing.freeFeature3': 'Edit destinations anytime',
    'pricing.freeFeature4': 'Google sign-in',
    'pricing.freeFeatureModify': '<strong>Unlimited destination changes</strong>',
    'pricing.freeFeatureSlug': '<strong>Custom aliases (included)</strong>',
    'pricing.freeCta': 'Continue with Google',
    'pricing.bestValue': 'Best value',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ lifetime',
    'pricing.proLede': 'Unlimited links, built for creators and teams.',
    'pricing.proFeature1': 'Unlimited links',
    'pricing.proFeature2': 'Priority Support',
    'pricing.proFeature3': 'High-performance Redirects',
    'pricing.proFeature4': 'Commercial License',
    'pricing.proCta': 'Get Pro',
    'pricing.proCtaAlt': 'Sign in to upgrade',
    'pricing.proNote': 'After purchase, your account is upgraded instantly or within a short delay.',
    'pricing.faqTitle': 'FAQ',
    'pricing.faqQ1': 'Is Pro a subscription?',
    'pricing.faqA1': 'No. Pro is a one-time purchase. You keep it for life.',
    'pricing.faqQ2': 'What happens if I hit the free limit?',
    'pricing.faqA2': 'You can still edit and delete links. You’ll need Pro to create more than 5.',
    'pricing.faqQ3': 'Do my existing links keep working?',
    'pricing.faqA3': 'Yes. Your short links keep redirecting even if you stop using the dashboard.',
    'pricing.faqQ4': 'Can I change destinations later?',
    'pricing.faqA4': 'Yes, you can update any link destination at any time.',
    'footer.legalNotice': 'Legal Notice',
    'footer.privacy': 'Privacy',
    'footer.terms': 'Terms of Sale',
    'footer.copyright': 'by Bitek',
    'footer.promo': '✨ More free tools at bitek.fr ✨',
    'hero.badge.editable': 'Unlimited edits',
    'hero.badge.noAds': 'No ads',
    'hero.badge.fast': 'Instant redirect',
    'hero.badge.secure': 'Secure & Private',
    'pricing.analyticsTitle': 'Click Statistics',
    'pricing.analyticsDesc': 'View click data',
    'pricing.qrCodeTitle': 'Automatic QR Code',
    'pricing.qrCodeDesc': 'Download QR Code',
    'pricing.passwordTitle': 'Password Protected Links',
    'pricing.passwordDesc': 'Restrict access',
    'folder.create': 'New folder...',
    'folder.name': 'Folder name',
    'folder.uncategorized': 'Uncategorized',
    'folder.empty': 'Empty (Drag links here)',
  },
  fr: {
    'brand.name': 'ShortURL',
    'brand.console': 'Console',
    'nav.pricing': 'Tarifs',
    'nav.signIn': 'Connexion',
    'auth.heroTitle': 'Raccourcissez, partagez, modifiez.',
    'auth.heroSubtitle': 'Créez des liens courts sur <strong>{{domain}}</strong> et modifiez-les à tout moment.',
    'hero.inputPlaceholder': 'Collez votre lien long ici...',
    'hero.shortenButton': 'Raccourcir',
    'hero.noCreditCard': 'Pas de carte bancaire requise. <strong>Gratuit à vie.</strong>',
    'auth.feature.privateLinks': 'Liens privés (un compte = vos liens)',
    'auth.feature.customSlug': 'Slug personnalisable',
    'auth.feature.editAnytime': 'Modifiable à tout moment',
    'auth.note': "Un compte est nécessaire pour protéger vos liens et afficher uniquement vos slugs.",
    'auth.loginTitle': 'Connexion',
    'auth.registerTitle': 'Créer un compte',
    'auth.loginSubtitle': 'Connectez-vous pour accéder à votre console.',
    'auth.registerSubtitle': 'Créez un compte pour gérer vos liens en toute sécurité.',
    'auth.tipGoogle': 'Astuce : vous pouvez aussi utiliser “Continuer avec Google”.',
    'auth.email': 'Email',
    'auth.emailPlaceholder': 'vous@example.com',
    'auth.password': 'Mot de passe',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': 'Patientez…',
    'auth.signIn': 'Se connecter',
    'auth.createAccount': 'Créer le compte',
    'auth.or': 'ou',
    'auth.continueWithGoogle': 'Continuer avec Google',
    'auth.createFree': 'Créer un compte gratuitement',
    'auth.haveAccount': 'Déjà un compte ? Se connecter',
    'status.apiOnline': 'API connectée',
    'status.apiOffline': 'API hors ligne',
    'plan.free': 'Gratuit',
    'plan.pro': 'Pro',
    'plan.current': 'Offre actuelle',
    'plan.upgrade': 'Passer Pro',
    'plan.linksUsed': '{{used}} / {{limit}} liens utilisés',
    'plan.limitReachedTitle': 'Limite atteinte',
    'plan.limitReachedBody': 'Vous avez atteint la limite du plan gratuit. Passez Pro pour créer des liens illimités.',
    'plan.upgradeCta': 'Débloquer Pro',
    'action.logout': 'Se déconnecter',
    'action.shorten': 'Raccourcir un lien',
    'quickShorten.placeholder': 'Coller un lien à raccourcir...',
    'quickShorten.descLabel': 'Description',
    'quickShorten.codeLabel': 'Code',
    'quickShorten.codePlaceholder': 'Mot de passe (optionnel)',
    'quickShorten.submit': 'Raccourcir ce lien',
    'linkForm.createTitle': 'Créer un lien',
    'linkForm.editTitle': 'Modifier le lien',
    'folder.new': 'Nouveau dossier',
    'linkForm.destinationLabel': 'Destination (URL complète)',
    'linkForm.destinationPlaceholder': 'https://exemple.com/article',
    'linkForm.slugOptional': 'Alias personnalisé (optionnel)',
    'linkForm.slug': 'Alias',
    'linkForm.slugPlaceholder': 'mon-alias',
    'linkForm.preview': 'Aperçu :',
    'linkForm.save': 'Enregistrer',
    'linkForm.descriptionLabel': 'Description (optionnelle)',
    'linkForm.descriptionPlaceholder': 'Notes privées…',
    'linkForm.generating': 'En cours…',
    'linkForm.generate': 'Générer',
    'linkForm.update': 'Mettre à jour',
    'linkForm.cancel': 'Annuler',
    'linkForm.folderLabel': 'Dossier',
    'linkForm.noFolder': 'Aucun dossier',
    'stats.linksCount': 'Nombre de liens',
    'stats.lastUpdated': 'Dernière mise à jour',
    'search.label': 'Rechercher un lien',
    'search.placeholder': 'Rechercher...',
    'loading.links': 'Chargement des liens…',
    'toast.linksLoadFailed': 'Impossible de charger les liens',
    'toast.linkCreated': 'Lien créé',
    'toast.createFailed': 'Création impossible. Vérifiez le slug.',
    'toast.planLimit': 'Limite du plan gratuit atteinte. Passez Pro pour créer plus de liens.',
    'toast.linkUpdated': 'Lien mis à jour',
    'toast.updateFailed': 'Mise à jour impossible',
    'confirm.delete': 'Supprimer {{slug}} ?',
    'toast.linkDeleted': 'Lien supprimé',
    'toast.deleteFailed': 'Suppression impossible',
    'table.title': 'Vos liens',
    'table.subtitle': 'Gérez vos slugs et mettez à jour les destinations en 1 clic.',
    'table.emptyTitle': 'Vos liens',
    'table.emptySubtitle': 'Aucun lien pour le moment. Créez votre premier raccourci.',
    'fab.createFirst': 'Créer votre premier lien',
    'table.slug': 'Alias',
    'table.destination': 'Destination',
    'table.clicks': 'Clics',
    'table.updated': 'Mise à jour',
    'table.actions': 'Actions',
    'table.copy': 'Copier',
    'table.edit': 'Éditer',
    'table.delete': 'Supprimer',
    'table.deleting': 'Suppression…',
    'table.linkSingular': 'lien',
    'table.linkPlural': 'liens',
    'table.updatedLabel': 'Mise à jour',
    'pricing.badge': 'Simple, rapide, conçu pour vous',
    'pricing.title': 'Raccourcissez, partagez, modifiez.',
    'pricing.subtitle': 'Créez des liens courts sur <strong>{{domain}}</strong> et modifiez-les à tout moment.',
    'pricing.exampleTitle': 'Exemple en action',
    'pricing.exampleSubtitle': 'Transformez une longue URL en lien court propre et éditable.',
    'pricing.exampleHint': 'Ajoutez une capture dans /admin-spa/public/shorturl-preview.png.',
    'pricing.ctaStart': 'Commencer gratuitement',
    'pricing.ctaBuyPro': 'Acheter Pro',
    'pricing.disclaimer': 'Pro est un achat unique. Aucun abonnement.',
    'pricing.plansLabel': 'Offres',
    'pricing.freeTitle': 'Gratuit',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ à vie',
    'pricing.freeLede': 'Parfait pour débuter et partager quelques liens.',
    'pricing.freeFeature1': 'Jusqu’à 5 liens',
    'pricing.freeFeature2': 'Slugs personnalisés',
    'pricing.freeFeature3': 'Modifier les destinations à tout moment',
    'pricing.freeFeature4': 'Connexion Google',
    'pricing.freeFeatureModify': '<strong>Modification de destination illimitée</strong>',
    'pricing.freeFeatureSlug': '<strong>Alias personnalisés (inclus)</strong>',
    'pricing.freeCta': 'Continuer avec Google',
    'pricing.bestValue': 'Meilleure offre',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ à vie',
    'pricing.proLede': 'Liens illimités, pensé pour les créateurs et pros.',
    'pricing.proFeature1': 'Liens illimités',
    'pricing.proFeature2': 'Support prioritaire',
    'pricing.proFeature3': 'Redirections haute performance',
    'pricing.proFeature4': 'Licence commerciale',
    'pricing.proCta': 'Passer Pro',
    'pricing.proCtaAlt': 'Se connecter pour upgrader',
    'pricing.proNote': 'Après achat, votre compte passe Pro instantanément ou sous peu.',
    'pricing.faqTitle': 'FAQ',
    'pricing.faqQ1': 'Pro est-il un abonnement ?',
    'pricing.faqA1': 'Non. Pro est un achat unique : vous le gardez à vie.',
    'pricing.faqQ2': 'Que se passe-t-il si j’atteins la limite ?',
    'pricing.faqA2': 'Vous pouvez toujours modifier/supprimer. Pro est requis pour créer plus de 5 liens.',
    'pricing.faqQ3': 'Mes liens continuent-ils de fonctionner ?',
    'pricing.faqA3': 'Oui. Vos liens continuent de rediriger même sans utiliser la console.',
    'pricing.faqQ4': 'Puis-je changer la destination plus tard ?',
    'pricing.faqA4': 'Oui, vous pouvez mettre à jour la destination à tout moment.',
    'footer.legalNotice': 'Mentions légales',
    'footer.privacy': 'Confidentialité',
    'footer.terms': 'CGV',
    'footer.copyright': 'par Bitek',
    'footer.promo': '✨ Plus d’outils gratuits sur bitek.fr ✨',
    'hero.badge.editable': 'Modifiable à volonté',
    'hero.badge.noAds': 'Pas de publicité',
    'hero.badge.fast': 'Redirection immédiate',
    'hero.badge.secure': 'Sécurisé & Privé',
    'pricing.analyticsTitle': 'Statistiques de clics',
    'pricing.analyticsDesc': 'Voir les clics',
    'pricing.qrCodeTitle': 'QR Code automatique',
    'pricing.qrCodeDesc': 'Télécharger le QR Code',
    'pricing.passwordTitle': 'Liens protégés par mot de passe',
    'pricing.passwordDesc': 'Restreindre l\'accès',
    'folder.create': 'Nouveau dossier...',
    'folder.name': 'Nom du dossier',
    'folder.uncategorized': 'Aucun dossier',
    'folder.empty': 'Vide (Glissez des liens ici)',
  },
  es: {
    'brand.name': 'ShortURL',
    'brand.console': 'Panel',
    'nav.pricing': 'Precios',
    'nav.signIn': 'Entrar',
    'auth.heroTitle': 'Acorta, comparte, actualiza.',
    'auth.heroSubtitle': 'Crea enlaces cortos en <strong>{{domain}}</strong> y edítalos cuando quieras.',
    'hero.inputPlaceholder': 'Pega tu enlace largo aquí...',
    'hero.shortenButton': 'Acortar',
    'hero.noCreditCard': 'Sin tarjeta. <strong>Gratis para siempre.</strong>',
    'auth.feature.privateLinks': 'Enlaces privados (una cuenta = tus enlaces)',
    'auth.feature.customSlug': 'Slug personalizado',
    'auth.feature.editAnytime': 'Editable en cualquier momento',
    'auth.note': 'Se requiere una cuenta para proteger tus enlaces y mostrar solo tus slugs.',
    'auth.loginTitle': 'Iniciar sesión',
    'auth.registerTitle': 'Crear cuenta',
    'auth.loginSubtitle': 'Inicia sesión para acceder a tu panel.',
    'auth.registerSubtitle': 'Crea una cuenta para gestionar tus enlaces de forma segura.',
    'auth.tipGoogle': 'Consejo: también puedes usar “Continuar con Google”.',
    'auth.email': 'Email',
    'auth.emailPlaceholder': 'tu@ejemplo.com',
    'auth.password': 'Contraseña',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': 'Espera…',
    'auth.signIn': 'Entrar',
    'auth.createAccount': 'Crear cuenta',
    'auth.or': 'o',
    'auth.continueWithGoogle': 'Continuar con Google',
    'auth.createFree': 'Crear una cuenta gratis',
    'auth.haveAccount': '¿Ya tienes cuenta? Entrar',
    'status.apiOnline': 'API en línea',
    'status.apiOffline': 'API sin conexión',
    'plan.free': 'Gratis',
    'plan.pro': 'Pro',
    'plan.current': 'Plan actual',
    'plan.upgrade': 'Mejorar',
    'plan.linksUsed': '{{used}} / {{limit}} enlaces usados',
    'plan.limitReachedTitle': 'Límite alcanzado',
    'plan.limitReachedBody': 'Has alcanzado el límite del plan Gratis. Pásate a Pro para enlaces ilimitados.',
    'plan.upgradeCta': 'Desbloquear Pro',
    'action.logout': 'Salir',
    'action.shorten': 'Acortar enlace',
    'quickShorten.placeholder': 'Pegar enlace a acortar...',
    'quickShorten.descLabel': 'Descripción',
    'quickShorten.codeLabel': 'Código',
    'quickShorten.codePlaceholder': 'Contraseña (opcional)',
    'quickShorten.submit': 'Acortar este enlace',
    'linkForm.createTitle': 'Crear enlace',
    'linkForm.editTitle': 'Editar enlace',
    'folder.new': 'Nueva carpeta',
    'linkForm.destinationLabel': 'Destino (URL completa)',
    'linkForm.destinationPlaceholder': 'https://ejemplo.com/articulo',
    'linkForm.slugOptional': 'Alias personalizado (opcional)',
    'linkForm.slug': 'Alias',
    'linkForm.slugPlaceholder': 'mi-alias',
    'linkForm.preview': 'Vista previa:',
    'linkForm.save': 'Guardar',
    'linkForm.descriptionLabel': 'Descripción (opcional)',
    'linkForm.descriptionPlaceholder': 'Notas privadas…',
    'linkForm.generating': 'Procesando…',
    'linkForm.generate': 'Generar',
    'linkForm.update': 'Actualizar',
    'linkForm.cancel': 'Cancelar',
    'linkForm.folderLabel': 'Carpeta',
    'linkForm.noFolder': 'Sin carpeta',
    'stats.linksCount': 'Enlaces',
    'stats.lastUpdated': 'Última actualización',
    'search.label': 'Buscar enlace',
    'search.placeholder': 'Buscar...',
    'loading.links': 'Cargando enlaces…',
    'toast.linksLoadFailed': 'No se pudieron cargar los enlaces',
    'toast.linkCreated': 'Enlace creado',
    'toast.createFailed': 'No se pudo crear. Revisa el slug.',
    'toast.planLimit': 'Límite del plan Gratis alcanzado. Pásate a Pro para crear más enlaces.',
    'toast.linkUpdated': 'Enlace actualizado',
    'toast.updateFailed': 'No se pudo actualizar',
    'confirm.delete': '¿Eliminar {{slug}}?',
    'toast.linkDeleted': 'Enlace eliminado',
    'toast.deleteFailed': 'No se pudo eliminar',
    'table.title': 'Tus enlaces',
    'table.subtitle': 'Gestiona tus slugs y actualiza destinos en 1 clic.',
    'table.emptyTitle': 'Tus enlaces',
    'table.emptySubtitle': 'Aún no hay enlaces. Crea el primero.',
    'fab.createFirst': 'Crea tu primer enlace',
    'table.slug': 'Alias',
    'table.destination': 'Destino',
    'table.clicks': 'Clics',
    'table.updated': 'Actualizado',
    'table.actions': 'Acciones',
    'table.copy': 'Copiar',
    'table.edit': 'Editar',
    'table.delete': 'Eliminar',
    'table.deleting': 'Eliminando…',
    'table.linkSingular': 'enlace',
    'table.linkPlural': 'enlaces',
    'table.updatedLabel': 'Actualizado',
    'pricing.badge': 'Simple, rápido, hecho para ti',
    'pricing.title': 'Acorta, comparte, actualiza.',
    'pricing.subtitle': 'Crea enlaces cortos en <strong>{{domain}}</strong> y edítalos cuando quieras.',
    'pricing.exampleTitle': 'Ejemplo en acción',
    'pricing.exampleSubtitle': 'Convierte URLs largas en enlaces cortos limpios y editables.',
    'pricing.exampleHint': 'Coloca una captura en /admin-spa/public/shorturl-preview.png.',
    'pricing.ctaStart': 'Empezar gratis',
    'pricing.ctaBuyPro': 'Comprar Pro',
    'pricing.disclaimer': 'Pro es un pago único. Sin suscripciones.',
    'pricing.plansLabel': 'Planes',
    'pricing.freeTitle': 'Gratis',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ para siempre',
    'pricing.freeLede': 'Perfecto para empezar y compartir algunos enlaces.',
    'pricing.freeFeature1': 'Hasta 5 enlaces',
    'pricing.freeFeature2': 'Slugs personalizados',
    'pricing.freeFeature3': 'Edita destinos cuando quieras',
    'pricing.freeFeature4': 'Inicio de sesión con Google',
    'pricing.freeFeatureModify': '<strong>Cambios de destino ilimitados</strong>',
    'pricing.freeFeatureSlug': '<strong>Alias personalizados</strong>',
    'pricing.freeCta': 'Continuar con Google',
    'pricing.bestValue': 'Mejor opción',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ de por vida',
    'pricing.proLede': 'Enlaces ilimitados para creadores y equipos.',
    'pricing.proFeature1': 'Enlaces ilimitados',
    'pricing.proFeature2': 'Soporte prioritario',
    'pricing.proFeature3': 'Redirecciones de alto rendimiento',
    'pricing.proFeature4': 'Licencia comercial',
    'pricing.proCta': 'Obtener Pro',
    'pricing.proCtaAlt': 'Entrar para mejorar',
    'pricing.proNote': 'Tras el pago, tu cuenta se mejora al instante o en poco tiempo.',
    'pricing.faqTitle': 'FAQ',
    'pricing.faqQ1': '¿Pro es una suscripción?',
    'pricing.faqA1': 'No. Pro es un pago único. Lo tienes para siempre.',
    'pricing.faqQ2': '¿Qué pasa si llego al límite gratis?',
    'pricing.faqA2': 'Puedes editar y borrar. Necesitas Pro para crear más de 5.',
    'pricing.faqQ3': '¿Mis enlaces siguen funcionando?',
    'pricing.faqA3': 'Sí. Los enlaces siguen redirigiendo aunque no uses el panel.',
    'pricing.faqQ4': '¿Puedo cambiar destinos luego?',
    'pricing.faqA4': 'Sí, puedes actualizar cualquier destino cuando quieras.',
    'footer.legalNotice': 'Aviso legal',
    'footer.privacy': 'Privacidad',
    'footer.terms': 'Condiciones de venta',
    'footer.copyright': 'por Bitek',
    'footer.promo': '✨ Más herramientas gratis en bitek.fr ✨',
    'hero.badge.editable': 'Edición ilimitada',
    'hero.badge.noAds': 'Sin publicidad',
    'hero.badge.fast': 'Redirección inmediata',
    'hero.badge.secure': 'Seguro y privado',
    'pricing.analyticsTitle': 'Estadísticas de clics',
    'pricing.analyticsDesc': 'Ver datos de clics',
    'pricing.qrCodeTitle': 'Código QR automático',
    'pricing.qrCodeDesc': 'Descargar código QR',
    'pricing.passwordTitle': 'Enlaces protegidos con contraseña',
    'pricing.passwordDesc': 'Restringir el acceso',
    'folder.create': 'Nueva carpeta...',
    'folder.name': 'Nombre de la carpeta',
    'folder.uncategorized': 'Sin carpeta',
    'folder.empty': 'Vacío (Arrastra enlaces aquí)',
  },
  pt: {
    'brand.name': 'ShortURL',
    'brand.console': 'Painel',
    'nav.pricing': 'Preços',
    'nav.signIn': 'Entrar',
    'auth.heroTitle': 'Encurte, compartilhe, atualize.',
    'auth.heroSubtitle': 'Crie links curtos em <strong>{{domain}}</strong> e edite-os a qualquer hora.',
    'hero.inputPlaceholder': 'Cole seu link longo aqui...',
    'hero.shortenButton': 'Encurtar',
    'hero.noCreditCard': 'Sem cartão de crédito. <strong>Gratuito para sempre.</strong>',
    'auth.feature.privateLinks': 'Links privados (uma conta = seus links)',
    'auth.feature.customSlug': 'Slug personalizado',
    'auth.feature.editAnytime': 'Editável a qualquer momento',
    'auth.note': 'Uma conta é necessária para proteger seus links e mostrar apenas seus slugs.',
    'auth.loginTitle': 'Entrar',
    'auth.registerTitle': 'Criar conta',
    'auth.loginSubtitle': 'Entre para acessar seu painel.',
    'auth.registerSubtitle': 'Crie uma conta para gerenciar seus links com segurança.',
    'auth.tipGoogle': 'Dica: você também pode usar “Continuar com o Google”.',
    'auth.email': 'Email',
    'auth.emailPlaceholder': 'voce@exemplo.com',
    'auth.password': 'Senha',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': 'Aguarde…',
    'auth.signIn': 'Entrar',
    'auth.createAccount': 'Criar conta',
    'auth.or': 'ou',
    'auth.continueWithGoogle': 'Continuar com o Google',
    'auth.createFree': 'Criar conta grátis',
    'auth.haveAccount': 'Já tem conta? Entrar',
    'status.apiOnline': 'API online',
    'status.apiOffline': 'API offline',
    'plan.free': 'Gratuito',
    'plan.pro': 'Pro',
    'plan.current': 'Plano atual',
    'plan.upgrade': 'Upgrade',
    'plan.linksUsed': '{{used}} / {{limit}} links usados',
    'plan.limitReachedTitle': 'Limite atingido',
    'plan.limitReachedBody': 'Você atingiu o limite do plano gratuito. Faça upgrade para Pro e crie links ilimitados.',
    'plan.upgradeCta': 'Desbloquear Pro',
    'action.logout': 'Sair',
    'action.shorten': 'Encurtar link',
    'quickShorten.placeholder': 'Colar link para encurtar...',
    'quickShorten.descLabel': 'Descrição',
    'quickShorten.codeLabel': 'Código',
    'quickShorten.codePlaceholder': 'Senha (opcional)',
    'quickShorten.submit': 'Encurtar este link',
    'linkForm.createTitle': 'Criar link',
    'linkForm.editTitle': 'Editar link',
    'folder.new': 'Nova pasta',
    'linkForm.destinationLabel': 'Destino (URL completa)',
    'linkForm.destinationPlaceholder': 'https://exemplo.com/artigo',
    'linkForm.slugOptional': 'Alias personalizado (opcional)',
    'linkForm.slug': 'Alias',
    'linkForm.slugPlaceholder': 'meu-alias',
    'linkForm.preview': 'Prévia:',
    'linkForm.save': 'Salvar',
    'linkForm.descriptionLabel': 'Descrição (opcional)',
    'linkForm.descriptionPlaceholder': 'Notas privadas…',
    'linkForm.generating': 'Processando…',
    'linkForm.generate': 'Gerar',
    'linkForm.update': 'Atualizar',
    'linkForm.cancel': 'Cancelar',
    'linkForm.folderLabel': 'Pasta',
    'linkForm.noFolder': 'Sem pasta',
    'stats.linksCount': 'Links',
    'stats.lastUpdated': 'Última atualização',
    'search.label': 'Buscar link',
    'search.placeholder': 'Pesquisar...',
    'loading.links': 'Carregando links…',
    'toast.linksLoadFailed': 'Não foi possível carregar os links',
    'toast.linkCreated': 'Link criado',
    'toast.createFailed': 'Falha ao criar. Verifique o slug.',
    'toast.planLimit': 'Limite do plano gratuito atingido. Faça upgrade para Pro para criar mais links.',
    'toast.linkUpdated': 'Link atualizado',
    'toast.updateFailed': 'Falha ao atualizar',
    'confirm.delete': 'Excluir {{slug}}?',
    'toast.linkDeleted': 'Link excluído',
    'toast.deleteFailed': 'Falha ao excluir',
    'table.title': 'Seus links',
    'table.subtitle': 'Gerencie seus slugs e atualize destinos em 1 clique.',
    'table.emptyTitle': 'Seus links',
    'table.emptySubtitle': 'Ainda não há links. Crie o primeiro.',
    'fab.createFirst': 'Crie seu primeiro link',
    'table.slug': 'Alias',
    'table.destination': 'Destino',
    'table.clicks': 'Cliques',
    'table.updated': 'Atualizado',
    'table.actions': 'Ações',
    'table.copy': 'Copiar',
    'table.edit': 'Editar',
    'table.delete': 'Excluir',
    'table.deleting': 'Excluindo…',
    'table.linkSingular': 'link',
    'table.linkPlural': 'links',
    'table.updatedLabel': 'Atualizado',
    'pricing.badge': 'Simples, rápido, feito para você',
    'pricing.title': 'Encurte, compartilhe, atualize.',
    'pricing.subtitle': 'Crie links curtos em <strong>{{domain}}</strong> e edite-os a qualquer hora.',
    'pricing.exampleTitle': 'Exemplo em ação',
    'pricing.exampleSubtitle': 'Transforme URLs longas em links curtos e editáveis.',
    'pricing.exampleHint': 'Coloque uma captura em /admin-spa/public/shorturl-preview.png.',
    'pricing.ctaStart': 'Começar grátis',
    'pricing.ctaBuyPro': 'Comprar Pro',
    'pricing.disclaimer': 'Pro é compra única. Sem assinaturas.',
    'pricing.plansLabel': 'Planos',
    'pricing.freeTitle': 'Gratuito',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ para sempre',
    'pricing.freeLede': 'Perfeito para começar e compartilhar alguns links.',
    'pricing.freeFeature1': 'Até 5 links',
    'pricing.freeFeature2': 'Slugs personalizados',
    'pricing.freeFeature3': 'Edite destinos a qualquer momento',
    'pricing.freeFeature4': 'Login com Google',
    'pricing.freeFeatureModify': '<strong>Alterações de destino ilimitadas</strong>',
    'pricing.freeFeatureSlug': '<strong>Alias personalizados</strong>',
    'pricing.freeCta': 'Continuar com Google',
    'pricing.bestValue': 'Melhor escolha',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ vitalício',
    'pricing.proLede': 'Links ilimitados para criadores e equipes.',
    'pricing.proFeature1': 'Links ilimitados',
    'pricing.proFeature2': 'Suporte prioritário',
    'pricing.proFeature3': 'Redirecionamentos de alta performance',
    'pricing.proFeature4': 'Licença comercial',
    'pricing.proCta': 'Obter Pro',
    'pricing.proCtaAlt': 'Entrar para upgrade',
    'pricing.proNote': 'Após o pagamento, sua conta é atualizada imediatamente ou em pouco tempo.',
    'pricing.faqTitle': 'FAQ',
    'pricing.faqQ1': 'Pro é assinatura?',
    'pricing.faqA1': 'Não. Pro é compra única. Você fica com ele para sempre.',
    'pricing.faqQ2': 'O que acontece ao atingir o limite grátis?',
    'pricing.faqA2': 'Você pode editar e excluir. Precisa do Pro para criar mais de 5.',
    'pricing.faqQ3': 'Meus links continuam funcionando?',
    'pricing.faqA3': 'Sim. Seus links continuam redirecionando mesmo sem usar o painel.',
    'pricing.faqQ4': 'Posso mudar destinos depois?',
    'pricing.faqA4': 'Sim, você pode atualizar qualquer destino quando quiser.',
    'footer.legalNotice': 'Aviso legal',
    'footer.privacy': 'Privacidade',
    'footer.terms': 'Condições de venda',
    'footer.copyright': 'por Bitek',
    'footer.promo': '✨ Mais ferramentas grátis em bitek.fr ✨',
    'hero.badge.editable': 'Edição ilimitada',
    'hero.badge.noAds': 'Sem anúncios',
    'hero.badge.fast': 'Redirecionamento imediato',
    'hero.badge.secure': 'Seguro e privado',
    'pricing.analyticsTitle': 'Estatísticas de cliques',
    'pricing.analyticsDesc': 'Ver dados de cliques',
    'pricing.qrCodeTitle': 'QR Code automático',
    'pricing.qrCodeDesc': 'Baixar QR Code',
    'pricing.passwordTitle': 'Links protegidos por senha',
    'pricing.passwordDesc': 'Restringir acesso',
    'folder.create': 'Nova pasta...',
    'folder.name': 'Nome da pasta',
    'folder.uncategorized': 'Sem pasta',
    'folder.empty': 'Vazio (Arraste links aqui)',
  },
  de: {
    'brand.name': 'ShortURL',
    'brand.console': 'Konsole',
    'nav.pricing': 'Preise',
    'nav.signIn': 'Anmelden',
    'auth.heroTitle': 'Kurz. Teilen. Aktualisieren.',
    'auth.heroSubtitle': 'Kurze Links auf <strong>{{domain}}</strong> – jederzeit bearbeitbar.',
    'hero.inputPlaceholder': 'Fügen Sie Ihren langen Link hier ein...',
    'hero.shortenButton': 'Kürzen',
    'hero.noCreditCard': 'Ohne Kreditkarte. <strong>Für immer gratis.</strong>',
    'auth.feature.privateLinks': 'Private Links (ein Konto = deine Links)',
    'auth.feature.customSlug': 'Benutzerdefinierter Slug',
    'auth.feature.editAnytime': 'Jederzeit bearbeitbar',
    'auth.note': 'Ein Konto ist erforderlich, um deine Links zu schützen und nur deine Slugs anzuzeigen.',
    'auth.loginTitle': 'Anmelden',
    'auth.registerTitle': 'Konto erstellen',
    'auth.loginSubtitle': 'Melde dich an, um dein Dashboard zu öffnen.',
    'auth.registerSubtitle': 'Erstelle ein Konto, um deine Links sicher zu verwalten.',
    'auth.tipGoogle': 'Tipp: Du kannst auch “Mit Google fortfahren” verwenden.',
    'auth.email': 'E-Mail',
    'auth.emailPlaceholder': 'du@beispiel.de',
    'auth.password': 'Passwort',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': 'Bitte warten…',
    'auth.signIn': 'Anmelden',
    'auth.createAccount': 'Konto erstellen',
    'auth.or': 'oder',
    'auth.continueWithGoogle': 'Mit Google fortfahren',
    'auth.createFree': 'Kostenloses Konto erstellen',
    'auth.haveAccount': 'Schon ein Konto? Anmelden',
    'status.apiOnline': 'API online',
    'status.apiOffline': 'API offline',
    'plan.free': 'Kostenlos',
    'plan.pro': 'Pro',
    'plan.current': 'Aktueller Plan',
    'plan.upgrade': 'Upgrade',
    'plan.linksUsed': '{{used}} / {{limit}} Links genutzt',
    'plan.limitReachedTitle': 'Limit erreicht',
    'plan.limitReachedBody': 'Du hast das Free-Limit erreicht. Upgrade auf Pro für unbegrenzte Links.',
    'plan.upgradeCta': 'Pro freischalten',
    'action.logout': 'Abmelden',
    'action.shorten': 'Link kürzen',
    'quickShorten.placeholder': 'Link zum Kürzen einfügen...',
    'quickShorten.descLabel': 'Beschreibung',
    'quickShorten.codeLabel': 'Code',
    'quickShorten.codePlaceholder': 'Passwort (optional)',
    'quickShorten.submit': 'Link kürzen',
    'linkForm.createTitle': 'Link erstellen',
    'linkForm.editTitle': 'Link bearbeiten',
    'folder.new': 'Neuer Ordner',
    'linkForm.destinationLabel': 'Ziel (vollständige URL)',
    'linkForm.destinationPlaceholder': 'https://beispiel.de/artikel',
    'linkForm.slugOptional': 'Benutzerdefinierter Alias (optional)',
    'linkForm.slug': 'Alias',
    'linkForm.slugPlaceholder': 'mein-alias',
    'linkForm.preview': 'Vorschau:',
    'linkForm.save': 'Speichern',
    'linkForm.descriptionLabel': 'Beschreibung (optional)',
    'linkForm.descriptionPlaceholder': 'Private Notizen…',
    'linkForm.generating': 'Wird verarbeitet…',
    'linkForm.generate': 'Erstellen',
    'linkForm.update': 'Aktualisieren',
    'linkForm.cancel': 'Abbrechen',
    'linkForm.folderLabel': 'Ordner',
    'linkForm.noFolder': 'Kein Ordner',
    'stats.linksCount': 'Links',
    'stats.lastUpdated': 'Zuletzt aktualisiert',
    'search.label': 'Link suchen',
    'search.placeholder': 'Suchen...',
    'loading.links': 'Links werden geladen…',
    'toast.linksLoadFailed': 'Links konnten nicht geladen werden',
    'toast.linkCreated': 'Link erstellt',
    'toast.createFailed': 'Erstellung fehlgeschlagen. Prüfe den Slug.',
    'toast.planLimit': 'Free-Limit erreicht. Upgrade auf Pro, um mehr Links zu erstellen.',
    'toast.linkUpdated': 'Link aktualisiert',
    'toast.updateFailed': 'Aktualisierung fehlgeschlagen',
    'confirm.delete': '{{slug}} löschen?',
    'toast.linkDeleted': 'Link gelöscht',
    'toast.deleteFailed': 'Löschen fehlgeschlagen',
    'table.title': 'Deine Links',
    'table.subtitle': 'Verwalte deine Slugs und aktualisiere Ziele mit 1 Klick.',
    'table.emptyTitle': 'Deine Links',
    'table.emptySubtitle': 'Noch keine Links. Erstelle deinen ersten.',
    'fab.createFirst': 'Erstelle deinen ersten Link',
    'table.slug': 'Alias',
    'table.destination': 'Ziel',
    'table.clicks': 'Klicks',
    'table.updated': 'Aktualisiert',
    'table.actions': 'Aktionen',
    'table.copy': 'Kopieren',
    'table.edit': 'Bearbeiten',
    'table.delete': 'Löschen',
    'table.deleting': 'Wird gelöscht…',
    'table.linkSingular': 'Link',
    'table.linkPlural': 'Links',
    'table.updatedLabel': 'Aktualisiert',
    'pricing.badge': 'Einfach, schnell, für dich gebaut',
    'pricing.title': 'Kurz. Teilen. Aktualisieren.',
    'pricing.subtitle': 'Kurze Links auf <strong>{{domain}}</strong> – jederzeit bearbeitbar.',
    'pricing.exampleTitle': 'Beispiel in Aktion',
    'pricing.exampleSubtitle': 'Mache aus langen URLs saubere, bearbeitbare Kurzlinks.',
    'pricing.exampleHint': 'Lege einen Screenshot in /admin-spa/public/shorturl-preview.png.',
    'pricing.ctaStart': 'Kostenlos starten',
    'pricing.ctaBuyPro': 'Pro kaufen',
    'pricing.disclaimer': 'Pro ist ein Einmalkauf. Keine Abos.',
    'pricing.plansLabel': 'Pläne',
    'pricing.freeTitle': 'Kostenlos',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ für immer',
    'pricing.freeLede': 'Perfekt zum Start und für ein paar Links.',
    'pricing.freeFeature1': 'Bis zu 5 Links',
    'pricing.freeFeature2': 'Eigene Slugs',
    'pricing.freeFeature3': 'Ziele jederzeit ändern',
    'pricing.freeFeature4': 'Google-Login',
    'pricing.freeFeatureModify': '<strong>Unbegrenzte Zieländerungen</strong>',
    'pricing.freeFeatureSlug': '<strong>Benutzerdefinierte Aliase</strong>',
    'pricing.freeCta': 'Mit Google fortfahren',
    'pricing.bestValue': 'Bestes Angebot',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ lebenslang',
    'pricing.proLede': 'Unbegrenzte Links für Creator und Teams.',
    'pricing.proFeature1': 'Unbegrenzte Links',
    'pricing.proFeature2': 'Priorisierter Support',
    'pricing.proFeature3': 'Hochleistungs-Weiterleitungen',
    'pricing.proFeature4': 'Kommerzielle Lizenz',
    'pricing.proCta': 'Pro holen',
    'pricing.proCtaAlt': 'Anmelden zum Upgrade',
    'pricing.proNote': 'Nach dem Kauf wird dein Konto sofort oder kurz danach upgegradet.',
    'pricing.faqTitle': 'FAQ',
    'pricing.faqQ1': 'Ist Pro ein Abo?',
    'pricing.faqA1': 'Nein. Pro ist ein Einmalkauf – für immer.',
    'pricing.faqQ2': 'Was passiert beim Free-Limit?',
    'pricing.faqA2': 'Du kannst weiter bearbeiten/löschen. Für mehr als 5 brauchst du Pro.',
    'pricing.faqQ3': 'Funktionieren bestehende Links weiter?',
    'pricing.faqA3': 'Ja. Deine Links leiten weiterhin weiter, auch ohne Dashboard.',
    'pricing.faqQ4': 'Kann ich Ziele später ändern?',
    'pricing.faqA4': 'Ja, jederzeit.',
    'footer.legalNotice': 'Impressum',
    'footer.privacy': 'Datenschutz',
    'footer.terms': 'AGB',
    'footer.copyright': 'von Bitek',
    'footer.promo': '✨ Mehr kostenlose Tools auf bitek.fr ✨',
    'hero.badge.editable': 'Unbegrenzt bearbeitbar',
    'hero.badge.noAds': 'Keine Werbung',
    'hero.badge.fast': 'Sofortige Weiterleitung',
    'hero.badge.secure': 'Secure & Private',
    'pricing.analyticsTitle': 'Klick-Statistiken',
    'pricing.analyticsDesc': 'Klickdaten anzeigen',
    'pricing.qrCodeTitle': 'Automatischer QR-Code',
    'pricing.qrCodeDesc': 'QR-Code herunterladen',
    'pricing.passwordTitle': 'Passwortgeschützte Links',
    'pricing.passwordDesc': 'Zugriff einschränken',
    'folder.create': 'Neuer Ordner...',
    'folder.name': 'Ordnername',
    'folder.uncategorized': 'Ohne Ordner',
    'folder.empty': 'Leer (Links hierher ziehen)',
  },
  ar: {
    'brand.name': 'ShortURL',
    'brand.console': 'لوحة التحكم',
    'nav.pricing': 'الأسعار',
    'nav.signIn': 'تسجيل الدخول',
    'auth.heroTitle': 'اختصر، شارك، حدّث.',
    'auth.heroSubtitle': 'أنشئ روابط قصيرة على <strong>{{domain}}</strong> وغيّر الوجهة في أي وقت.',
    'hero.inputPlaceholder': 'الصق الرابط الطويل هنا...',
    'hero.shortenButton': 'اختصار',
    'hero.noCreditCard': 'لا بطاقة ائتمان مطلوبة. <strong>مجاني للأبد.</strong>',
    'auth.feature.privateLinks': 'روابط خاصة (حساب واحد = روابطك)',
    'auth.feature.customSlug': 'Slug مخصص',
    'auth.feature.editAnytime': 'قابل للتعديل في أي وقت',
    'auth.note': 'يلزم حساب لحماية روابطك وإظهار slugs الخاصة بك فقط.',
    'auth.loginTitle': 'تسجيل الدخول',
    'auth.registerTitle': 'إنشاء حساب',
    'auth.loginSubtitle': 'سجّل الدخول للوصول إلى لوحتك.',
    'auth.registerSubtitle': 'أنشئ حسابًا لإدارة روابطك بأمان.',
    'auth.tipGoogle': 'نصيحة: يمكنك أيضًا استخدام “المتابعة باستخدام Google”.',
    'auth.email': 'البريد الإلكتروني',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.password': 'كلمة المرور',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': 'يرجى الانتظار…',
    'auth.signIn': 'دخول',
    'auth.createAccount': 'إنشاء حساب',
    'auth.or': 'أو',
    'auth.continueWithGoogle': 'المتابعة باستخدام Google',
    'auth.createFree': 'إنشاء حساب مجاني',
    'auth.haveAccount': 'لديك حساب؟ تسجيل الدخول',
    'status.apiOnline': 'API متصل',
    'status.apiOffline': 'API غير متصل',
    'plan.free': 'مجاني',
    'plan.pro': 'Pro',
    'plan.current': 'الخطة الحالية',
    'plan.upgrade': 'الترقية',
    'plan.linksUsed': '{{used}} / {{limit}} روابط مستخدمة',
    'plan.limitReachedTitle': 'تم الوصول للحد',
    'plan.limitReachedBody': 'لقد وصلت لحد الخطة المجانية. قم بالترقية إلى Pro لإنشاء روابط غير محدودة.',
    'plan.upgradeCta': 'فتح Pro',
    'action.logout': 'تسجيل الخروج',
    'action.shorten': 'تقصير رابط',
    'quickShorten.placeholder': 'الصق رابطًا للتقصير...',
    'quickShorten.descLabel': 'الوصف',
    'quickShorten.codeLabel': 'الرمز',
    'quickShorten.codePlaceholder': 'كلمة المرور (اختياري)',
    'quickShorten.submit': 'تقصير هذا الرابط',
    'linkForm.createTitle': 'إنشاء رابط',
    'linkForm.editTitle': 'تعديل الرابط',
    'folder.new': 'مجلد جديد',
    'linkForm.destinationLabel': 'الوجهة (الرابط الكامل)',
    'linkForm.destinationPlaceholder': 'https://example.com/article',
    'linkForm.slugOptional': 'Slug مخصص (اختياري)',
    'linkForm.slug': 'Slug',
    'linkForm.slugPlaceholder': 'my-link',
    'linkForm.preview': 'معاينة:',
    'linkForm.save': 'حفظ',
    'linkForm.descriptionLabel': 'الوصف (اختياري)',
    'linkForm.descriptionPlaceholder': 'ملاحظات خاصة…',
    'linkForm.generating': 'جارٍ التنفيذ…',
    'linkForm.generate': 'إنشاء',
    'linkForm.update': 'تحديث',
    'linkForm.cancel': 'إلغاء',
    'linkForm.folderLabel': 'Folder',
    'linkForm.noFolder': 'No folder',
    'stats.linksCount': 'روابط',
    'stats.lastUpdated': 'آخر تحديث',
    'search.label': 'ابحث عن رابط',
    'search.placeholder': 'slug أو الوجهة',
    'loading.links': 'جارٍ تحميل الروابط…',
    'toast.linksLoadFailed': 'تعذر تحميل الروابط',
    'toast.linkCreated': 'تم إنشاء الرابط',
    'toast.createFailed': 'فشل الإنشاء. تحقق من الـ slug.',
    'toast.planLimit': 'تم الوصول لحد الخطة المجانية. قم بالترقية إلى Pro لإنشاء المزيد.',
    'toast.linkUpdated': 'تم تحديث الرابط',
    'toast.updateFailed': 'فشل التحديث',
    'confirm.delete': 'حذف {{slug}}؟',
    'toast.linkDeleted': 'تم حذف الرابط',
    'toast.deleteFailed': 'فشل الحذف',
    'table.title': 'روابطك',
    'table.subtitle': 'أدر slugs وحدّث الوجهات بنقرة واحدة.',
    'table.emptyTitle': 'روابطك',
    'table.emptySubtitle': 'لا توجد روابط بعد. أنشئ أول رابط.',
    'fab.createFirst': 'أنشئ أول رابط لك',
    'table.slug': 'Slug',
    'table.destination': 'الوجهة',
    'table.clicks': 'النقرات',
    'table.updated': 'تحديث',
    'table.actions': 'إجراءات',
    'table.copy': 'نسخ',
    'table.edit': 'تعديل',
    'table.delete': 'حذف',
    'table.deleting': 'جارٍ الحذف…',
    'table.linkSingular': 'رابط',
    'table.linkPlural': 'روابط',
    'table.updatedLabel': 'تحديث',
    'pricing.badge': 'بسيط وسريع ومصمم لك',
    'pricing.title': 'اختصر، شارك، حدّث.',
    'pricing.subtitle': 'أنشئ روابط قصيرة على <strong>{{domain}}</strong> وغيّر الوجهة في أي وقت.',
    'pricing.exampleTitle': 'مثال عملي',
    'pricing.exampleSubtitle': 'حوّل الروابط الطويلة إلى روابط قصيرة قابلة للتعديل.',
    'pricing.exampleHint': 'ضع لقطة في /admin-spa/public/shorturl-preview.png.',
    'pricing.ctaStart': 'ابدأ مجانًا',
    'pricing.ctaBuyPro': 'شراء Pro',
    'pricing.disclaimer': 'Pro شراء لمرة واحدة. بدون اشتراكات.',
    'pricing.plansLabel': 'الخطط',
    'pricing.freeTitle': 'مجاني',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ مدى الحياة',
    'pricing.freeLede': 'مناسب للبدء ومشاركة بعض الروابط.',
    'pricing.freeFeature1': 'حتى 5 روابط',
    'pricing.freeFeature2': 'Slugs مخصصة',
    'pricing.freeFeature3': 'تعديل الوجهة في أي وقت',
    'pricing.freeFeature4': 'تسجيل الدخول عبر Google',
    'pricing.freeFeatureModify': '<strong>تغييرات غير محدودة للوجهة</strong>',
    'pricing.freeFeatureSlug': '<strong>أسماء مستعارة مخصصة</strong>',
    'pricing.freeCta': 'المتابعة مع Google',
    'pricing.bestValue': 'أفضل قيمة',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ مدى الحياة',
    'pricing.proLede': 'روابط غير محدودة للمبدعين والفرق.',
    'pricing.proFeature1': 'روابط غير محدودة',
    'pricing.proFeature2': 'دعم أولوية',
    'pricing.proFeature3': 'أداء عالي',
    'pricing.proFeature4': 'رخصة تجارية',
    'pricing.proCta': 'الحصول على Pro',
    'pricing.proCtaAlt': 'سجّل الدخول للترقية',
    'pricing.proNote': 'بعد الشراء سيتم ترقية حسابك فورًا أو خلال فترة قصيرة.',
    'pricing.faqTitle': 'الأسئلة الشائعة',
    'pricing.faqQ1': 'هل Pro اشتراك؟',
    'pricing.faqA1': 'لا. Pro شراء لمرة واحدة مدى الحياة.',
    'pricing.faqQ2': 'ماذا يحدث عند حد الخطة المجانية؟',
    'pricing.faqA2': 'يمكنك التعديل والحذف. تحتاج Pro لإنشاء أكثر من 5 روابط.',
    'pricing.faqQ3': 'هل ستعمل الروابط الحالية؟',
    'pricing.faqA3': 'نعم. ستستمر الروابط في إعادة التوجيه حتى بدون لوحة التحكم.',
    'pricing.faqQ4': 'هل يمكنني تغيير الوجهات لاحقًا؟',
    'pricing.faqA4': 'نعم، في أي وقت.',
    'footer.legalNotice': 'الإشعار القانوني',
    'footer.privacy': 'الخصوصية',
    'footer.terms': 'شروط البيع',
    'footer.copyright': 'بواسطة Bitek',
    'footer.promo': '✨ أدوات مجانية أكثر على bitek.fr ✨',
    'hero.badge.editable': 'تعديل غير محدود',
    'hero.badge.noAds': 'بدون إعلانات',
    'hero.badge.fast': 'إعادة توجيه فورية',
    'hero.badge.secure': 'آمن وخاص',
    'pricing.analyticsTitle': 'إحصائيات النقرات',
    'pricing.analyticsDesc': 'عرض بيانات النقرات',
    'pricing.qrCodeTitle': 'رمز QR تلقائي',
    'pricing.qrCodeDesc': 'تحميل رمز QR',
    'pricing.passwordTitle': 'روابط محمية بكلمة مرور',
    'pricing.passwordDesc': 'تقييد الوصول',
    'folder.create': 'مجلد جديد...',
    'folder.name': 'اسم المجلد',
    'folder.uncategorized': 'بدون مجلد',
    'folder.empty': 'فارغ (اسحب الروابط هنا)',
  },
  zh: {
    'brand.name': 'ShortURL',
    'brand.console': '控制台',
    'nav.pricing': '定价',
    'nav.signIn': '登录',
    'auth.heroTitle': '缩短、分享、更新。',
    'auth.heroSubtitle': '在 <strong>{{domain}}</strong> 创建短链接，随时修改目标地址。',
    'hero.inputPlaceholder': '在此粘贴长链接...',
    'hero.shortenButton': '缩短',
    'hero.noCreditCard': '无需信用卡。<strong>永久免费。</strong>',
    'auth.feature.privateLinks': '私有链接（一个账号=你的链接）',
    'auth.feature.customSlug': '自定义 slug',
    'auth.feature.editAnytime': '随时可修改',
    'auth.note': '需要账号来保护你的链接并只显示你的 slugs。',
    'auth.loginTitle': '登录',
    'auth.registerTitle': '创建账号',
    'auth.loginSubtitle': '登录以进入控制台。',
    'auth.registerSubtitle': '创建账号以安全管理链接。',
    'auth.tipGoogle': '提示：也可以使用“使用 Google 继续”。',
    'auth.email': '邮箱',
    'auth.emailPlaceholder': 'you@example.com',
    'auth.password': '密码',
    'auth.passwordPlaceholder': '••••••',
    'auth.wait': '请稍候…',
    'auth.signIn': '登录',
    'auth.createAccount': '创建账号',
    'auth.or': '或',
    'auth.continueWithGoogle': '使用 Google 继续',
    'auth.createFree': '免费创建账号',
    'auth.haveAccount': '已有账号？登录',
    'status.apiOnline': 'API 正常',
    'status.apiOffline': 'API 离线',
    'plan.free': '免费',
    'plan.pro': 'Pro',
    'plan.current': '当前方案',
    'plan.upgrade': '升级',
    'plan.linksUsed': '已用 {{used}} / {{limit}} 个链接',
    'plan.limitReachedTitle': '已达上限',
    'plan.limitReachedBody': '你已达到免费方案上限。升级 Pro 以创建无限链接。',
    'plan.upgradeCta': '解锁 Pro',
    'action.logout': '退出登录',
    'action.shorten': '缩短链接',
    'quickShorten.placeholder': '粘贴链接以缩短...',
    'quickShorten.descLabel': '描述',
    'quickShorten.codeLabel': '代码',
    'quickShorten.codePlaceholder': '密码（可选）',
    'quickShorten.submit': '缩短此链接',
    'linkForm.createTitle': '创建链接',
    'linkForm.editTitle': '编辑链接',
    'folder.new': '新文件夹',
    'linkForm.destinationLabel': '目标地址（完整 URL）',
    'linkForm.destinationPlaceholder': 'https://example.com/article',
    'linkForm.slugOptional': '自定义 slug（可选）',
    'linkForm.slug': 'Slug',
    'linkForm.slugPlaceholder': 'my-link',
    'linkForm.preview': '预览：',
    'linkForm.save': '保存',
    'linkForm.descriptionLabel': '描述（可选）',
    'linkForm.descriptionPlaceholder': '私密备注…',
    'linkForm.generating': '处理中…',
    'linkForm.generate': '生成',
    'linkForm.update': '更新',
    'linkForm.cancel': '取消',
    'linkForm.folderLabel': '文件夹',
    'linkForm.noFolder': '无文件夹',
    'stats.linksCount': '链接数',
    'stats.lastUpdated': '最近更新',
    'search.label': '搜索链接',
    'search.placeholder': 'slug 或目标地址',
    'loading.links': '正在加载链接…',
    'toast.linksLoadFailed': '无法加载链接',
    'toast.linkCreated': '已创建链接',
    'toast.createFailed': '创建失败，请检查 slug。',
    'toast.planLimit': '免费方案已达上限。升级 Pro 以创建更多链接。',
    'toast.linkUpdated': '已更新链接',
    'toast.updateFailed': '更新失败',
    'confirm.delete': '删除 {{slug}}？',
    'toast.linkDeleted': '已删除链接',
    'toast.deleteFailed': '删除失败',
    'table.title': '你的链接',
    'table.subtitle': '管理 slug，一键更新目标地址。',
    'table.emptyTitle': '你的链接',
    'table.emptySubtitle': '暂无链接，先创建一个。',
    'fab.createFirst': '创建你的第一个链接',
    'table.slug': 'Slug',
    'table.destination': '目标地址',
    'table.clicks': '点击数',
    'table.updated': '已更新',
    'table.actions': '操作',
    'table.copy': '复制',
    'table.edit': '编辑',
    'table.delete': '删除',
    'table.deleting': '删除中…',
    'table.linkSingular': '个链接',
    'table.linkPlural': '个链接',
    'table.updatedLabel': '更新',
    'pricing.badge': '简单、快速、为你打造',
    'pricing.title': '缩短、分享、更新。',
    'pricing.subtitle': '在 <strong>{{domain}}</strong> 创建短链接，随时修改目标地址。',
    'pricing.exampleTitle': '示例展示',
    'pricing.exampleSubtitle': '将长链接变成干净且可编辑的短链接。',
    'pricing.exampleHint': '把截图放在 /admin-spa/public/shorturl-preview.png。',
    'pricing.ctaStart': '免费开始',
    'pricing.ctaBuyPro': '购买 Pro',
    'pricing.disclaimer': 'Pro 为一次性购买，无订阅。',
    'pricing.plansLabel': '方案',
    'pricing.freeTitle': '免费',
    'pricing.freePrice': '0€',
    'pricing.freePeriod': '/ 永久',
    'pricing.freeLede': '适合入门与少量分享。',
    'pricing.freeFeature1': '最多 5 个链接',
    'pricing.freeFeature2': '自定义 slug',
    'pricing.freeFeature3': '随时修改目标',
    'pricing.freeFeature4': 'Google 登录',
    'pricing.freeFeatureModify': '<strong>无限次更改目标地址</strong>',
    'pricing.freeFeatureSlug': '<strong>自定义别名</strong>',
    'pricing.freeCta': '使用 Google 继续',
    'pricing.bestValue': '超值推荐',
    'pricing.proTitle': 'Pro',
    'pricing.proPeriod': '/ 终身',
    'pricing.proLede': '无限链接，适合创作者和团队。',
    'pricing.proFeature1': '无限链接',
    'pricing.proFeature2': '优先支持',
    'pricing.proFeature3': '高性能跳转',
    'pricing.proFeature4': '商业授权',
    'pricing.proCta': '获取 Pro',
    'pricing.proCtaAlt': '登录后升级',
    'pricing.proNote': '购买后账户会立即或稍后自动升级。',
    'pricing.faqTitle': '常见问题',
    'pricing.faqQ1': 'Pro 是订阅吗？',
    'pricing.faqA1': '不是。Pro 为一次性购买，终身有效。',
    'pricing.faqQ2': '达到免费上限会怎样？',
    'pricing.faqA2': '仍可编辑/删除，但创建超过 5 个需要 Pro。',
    'pricing.faqQ3': '已有链接会继续生效吗？',
    'pricing.faqA3': '会。即使不再使用控制台，短链仍会继续跳转。',
    'pricing.faqQ4': '可以随时更改目标吗？',
    'pricing.faqA4': '可以，随时更新目标地址。',
    'footer.legalNotice': '法律声明',
    'footer.privacy': '隐私政策',
    'footer.terms': '销售条款',
    'footer.copyright': 'by Bitek',
    'footer.promo': '✨ 更多免费工具尽在 bitek.fr ✨',
    'hero.badge.editable': '无限次修改',
    'hero.badge.noAds': '无广告',
    'hero.badge.fast': '即时跳转',
    'hero.badge.secure': '安全隐私',
    'pricing.analyticsTitle': '点击统计',
    'pricing.analyticsDesc': '查看点击数据',
    'pricing.qrCodeTitle': '自动生成二维码',
    'pricing.qrCodeDesc': '下载二维码',
    'pricing.passwordTitle': '密码保护链接',
    'pricing.passwordDesc': '限制访问',
    'folder.create': '新建文件夹...',
    'folder.name': '文件夹名称',
    'folder.uncategorized': '未分类',
    'folder.empty': '空（拖拽链接至此）',
  },
};

export const getLanguage = (code: string | null | undefined): Lang => {
  // If a code is provided (from localStorage), use it
  if (code) {
    const normalized = code.toLowerCase().split('-')[0] as Lang;
    if (languages.some((lang) => lang.code === normalized)) {
      return normalized;
    }
  }

  // Otherwise, detect from browser language
  if (typeof navigator !== 'undefined' && navigator.language) {
    const browserLang = navigator.language.toLowerCase().split('-')[0] as Lang;
    if (languages.some((lang) => lang.code === browserLang)) {
      return browserLang;
    }
  }

  // Fallback to English
  return 'en';
};

export const translate = (lang: Lang, key: TranslationKey, vars?: Record<string, string>) => {
  const template = translations[lang]?.[key] ?? translations.en[key];
  if (!vars) return template;
  return Object.entries(vars).reduce(
    (acc, [varKey, value]) => acc.split(`{{${varKey}}}`).join(value),
    template
  );
};
